import { SetStateAction, useCallback } from "react";
import { fetchQuery, useQueryLoader, useRelayEnvironment } from "react-relay";
import { WIDGET_EMPLOYEE_ROLE_OVERVIEW_PREVIEW_QUERY } from "./WidgetEmployeeRoleOverviewPreview";
import { useDebouncedCallback } from "use-debounce";
import { useFocusEffect } from "@react-navigation/native";
import { WidgetEmployeeRoleOverviewPreviewQuery } from "./__generated__/WidgetEmployeeRoleOverviewPreviewQuery.graphql";

export function useWidgetEmployeeRoleOverviewPreviewQuery({
  storecastId,
  teamIds,
  setTeamIds: _setTeamIds,
}: {
  storecastId: string;
  teamIds: string[];
  setTeamIds(_: SetStateAction<string[]>): void;
}) {
  const [query, loadQuery] =
    useQueryLoader<WidgetEmployeeRoleOverviewPreviewQuery>(
      WIDGET_EMPLOYEE_ROLE_OVERVIEW_PREVIEW_QUERY
    );

  const environment = useRelayEnvironment();

  const loadPreviewQuery = useDebouncedCallback(
    async (newTeamIds: string[]) => {
      try {
        const variables = {
          storecastId,
          input: {
            teamIds: newTeamIds.length === 0 ? null : newTeamIds,
            size: "TWO_X_TWO" as const,
          },
        };
        await fetchQuery(
          environment,
          WIDGET_EMPLOYEE_ROLE_OVERVIEW_PREVIEW_QUERY,
          variables
        ).toPromise();
        loadQuery(variables, {
          fetchPolicy: "store-and-network",
        });
      } catch {}
    },
    300
  );
  useFocusEffect(
    useCallback(() => {
      loadPreviewQuery(teamIds);
    }, [teamIds, loadPreviewQuery])
  );

  const setTeamIds = useCallback(
    (action: SetStateAction<string[]>) => {
      _setTeamIds((prev) => {
        const newIds = typeof action === "function" ? action(prev) : action;
        loadPreviewQuery(newIds);
        return newIds;
      });
    },
    [_setTeamIds, loadPreviewQuery]
  );

  return [query, setTeamIds] as const;
}
